import { BOTTOM_BAR_FLOATING_OFFSET } from "@ui/components/BottomBar";
import { useElementHeight } from "@ui/hooks/use-element-height";
import { useScrollSentinel } from "@ui/hooks/use-scroll-sentinel";
import { type StatusBarStyle, useStatusBar } from "@ui/hooks/use-status-bar";
import { useViewportRange } from "@ui/hooks/use-viewport-range";
import { tv } from "@ui/lib/tv";
import { type CSSProperties, type ReactNode, useMemo } from "react";
import { FrameAlerts } from "./components/Alerts";
import { FrameBackground } from "./components/Background";
import { FrameBottomBar, type FrameBottomBarProps } from "./components/BottomBar";
import { FrameContent, type FrameContentMaxWidth } from "./components/Content";
import { FrameFooter } from "./components/Footer";
import { FrameOverlay } from "./components/Overlay";
import { FrameLeftPane, type FramePaneProps, FrameRightPane } from "./components/Pane";
import { FrameTopBar } from "./components/TopBar";
import { FrameContext, type FrameContextValue } from "./FrameContext";
import { useFrameSlots } from "./use-frame-slots";

const SLOT_COMPONENTS = {
  Background: FrameBackground,
  TopBar: FrameTopBar,
  LeftPane: FrameLeftPane,
  Content: FrameContent,
  RightPane: FrameRightPane,
  Footer: FrameFooter,
  BottomBar: FrameBottomBar,
  Alerts: FrameAlerts,
  Overlay: FrameOverlay,
} as const;

// Inertia resets (and on back/forward, restores) the scroll position of the document body
// plus any element flagged with the `scroll-region` attribute. Our mobile scroller is a
// custom overflow container rather than the document, so it must be flagged or every
// navigation would land mid-scroll. Spread as a record because `scroll-region` isn't a
// typed HTML attribute.
const SCROLL_REGION_ATTR: Record<string, string> = { "scroll-region": "" };

const frame = tv({
  slots: {
    // Mobile pins the chrome and makes `scroller` the scroll context (overflow-hidden here +
    // overflow-y-auto there), so pull-to-refresh / over-scroll bounces *under* the top bar
    // instead of rubber-banding the whole document and dragging the sticky bar down with it.
    // Single-column desktop reverts to natural document scroll — see the `splitView` variant.
    root: "relative flex h-[100dvh] flex-col overflow-hidden",
    background: "pointer-events-none absolute inset-0 -z-10 overflow-hidden",
    topBar: "z-(--z-chrome-top)",
    /** Wraps the columns and footer. The page's scroll context on mobile and on desktop
     *  splitView (`overscroll-contain` keeps the bounce inside it); single-column desktop
     *  reverts to document scroll. Flagged as a `scroll-region` for Inertia scroll resets. */
    scroller: "flex flex-1 flex-col min-h-0 min-w-0 overflow-y-auto overscroll-contain",
    /** Grows to fill the scroller so a short page still pushes the footer down to the
     *  viewport bottom. With tall content, intrinsic content height wins and the footer
     *  follows naturally in overflow. */
    columns: "flex flex-1 flex-col md:flex-row min-w-0",
    main: "flex flex-1 flex-col min-w-0",
    contentWrapper: "flex flex-1 flex-col min-w-0",
    leftPane: "flex flex-col md:border-r md:border-neutral-100",
    rightPane: "flex flex-col md:border-l md:border-neutral-100",
    alerts:
      "pointer-events-none fixed inset-x-0 top-0 z-(--z-alert) flex flex-col items-center gap-2 px-4 pt-safe-[1rem]",
  },
  variants: {
    contentMaxWidth: {
      xsmall: { contentWrapper: "mx-auto w-full max-w-[20rem]" },
      small: { contentWrapper: "mx-auto w-full max-w-[34rem]" },
      medium: { contentWrapper: "mx-auto w-full max-w-[48rem]" },
      large: { contentWrapper: "mx-auto w-full max-w-[64rem]" },
      xlarge: { contentWrapper: "mx-auto w-full max-w-container" },
      full: {},
    },
    contentCentred: {
      true: { main: "items-center justify-center", contentWrapper: "flex-none" },
      false: {},
    },
    contentPadded: {
      true: { contentWrapper: "px-5 md:px-8" },
      false: {},
    },
    /** See `Frame.Content`'s `fills`. The column chain defaults to `min-height: auto`, whose floor
     *  is the content's own height — that is what lets a long page push the scroller and scroll
     *  naturally. Zeroing it here instead caps every column at the viewport and hands scrolling to
     *  whatever container the content nominates. */
    contentFills: {
      true: {
        scroller: "overflow-hidden overscroll-none",
        columns: "min-h-0",
        main: "min-h-0",
        contentWrapper: "min-h-0",
      },
      false: {},
    },
    /** A floating bottom bar overlays the page instead of reserving in-flow space, so the
     *  scroller pads by its footprint (published as `--frame-bottombar-offset`) to keep the
     *  last item reachable. Mobile only — the bar is `md:hidden`. */
    floatingBottomBar: {
      true: { scroller: "pb-(--frame-bottombar-offset) md:pb-0" },
      false: {},
    },
    leftPaneWidth: {
      xsmall: { leftPane: "w-full md:w-2xs" },
      small: { leftPane: "w-full md:w-xs" },
      medium: { leftPane: "w-full md:w-sm" },
      large: { leftPane: "w-full md:w-md" },
      fit: { leftPane: "w-full md:w-fit" },
    },
    leftPanePadded: {
      true: { leftPane: "p-5 md:box-content md:p-8" },
      false: {},
    },
    /** How the left pane handles narrow viewports without flicker on hydration. `responsive`
     *  uses Tailwind's `md:` breakpoint to keep the DOM mounted but hidden below `md` —
     *  used by `collapse="drill"` and `collapse="bottomSheet"` so SSR'd HTML matches the
     *  intended layout regardless of viewport (no useViewportRange round-trip flicker). */
    leftPaneVisibility: {
      responsive: { leftPane: "hidden md:flex" },
      visible: {},
    },
    rightPaneWidth: {
      xsmall: { rightPane: "w-full md:w-2xs" },
      small: { rightPane: "w-full md:w-xs" },
      medium: { rightPane: "w-full md:w-sm" },
      large: { rightPane: "w-full md:w-md" },
      fit: { rightPane: "w-full md:w-fit" },
    },
    rightPanePadded: {
      true: { rightPane: "p-5 md:box-content md:p-8" },
      false: {},
    },
    /** When a pane is visible at md+, the body region becomes the scroll context: panes
     *  stick to its top with their own internal overflow when content exceeds the viewport,
     *  main flows naturally beside them, and the footer follows after the columns inside
     *  the same scroll. Single-column pages stay with natural page-level scroll, footer at
     *  the end of the body. */
    splitView: {
      // Mobile is always a contained inner scroller (base slots). On desktop:
      //  - splitView keeps containment so the panes can sticky to the scroller's top.
      //  - single-column reverts to natural document scroll (root grows; scroller doesn't clip).
      true: {
        scroller: "md:overscroll-none",
        leftPane:
          "md:sticky md:top-0 md:h-[calc(100dvh-var(--frame-topbar-h,5.5rem))] md:overflow-y-auto md:flex-shrink-0",
        rightPane:
          "md:sticky md:top-0 md:h-[calc(100dvh-var(--frame-topbar-h,5.5rem))] md:overflow-y-auto md:flex-shrink-0",
      },
      false: {
        root: "md:h-auto md:min-h-[100dvh] md:overflow-visible",
        scroller: "md:overflow-visible md:overscroll-auto",
      },
    },
    /** Whether the top bar pins to the viewport top.
     *
     *  - `true` (default): sticky on all viewports.
     *  - `false`: sticky on mobile only — scrolls away with content on `md+`. Use this on
     *    pages with a full-bleed desktop decoration (e.g. the profile swoosh) so the
     *    bar moves with the page on desktop instead of letting content slide behind it.
     *
     *  Mobile is always sticky: the mobile top bar typically carries back-button or
     *  page context that needs to stay reachable mid-scroll, so non-sticky on mobile
     *  isn't a supported configuration. */
    stickyTopBar: {
      true: { topBar: "sticky top-0" },
      false: { topBar: "sticky top-0 md:static" },
    },
  },
  defaultVariants: {
    contentMaxWidth: "full",
    contentCentred: false,
    contentFills: false,
    contentPadded: true,
    splitView: false,
    stickyTopBar: true,
    leftPaneVisibility: "visible",
  },
});

export interface FrameProps {
  children: ReactNode;
  /** Status bar style — drives `<meta name="theme-color">` (web/PWA) and StatusBar
   *  plugin (Capacitor, when shells land). Default `dark` (= dark text on light bg). */
  statusBarStyle?: StatusBarStyle;
  /** Background colour for the OS status bar / theme-color meta. Default white. */
  statusBarColor?: string;
  className?: string;
}

/** Compound primitive for page chrome. Slots are matched by component identity
 *  on direct children — order in JSX doesn't matter. Frame parses children once and
 *  arranges each slot in the correct region.
 *
 *  See the slot components and `system/Frame/__stories__/Frame.stories.tsx` for the
 *  full vocabulary. The simplest valid Frame is `<Frame><Frame.Content>...` — every
 *  other slot is optional. */
function FrameRoot({ children, statusBarStyle, statusBarColor, className }: FrameProps) {
  useStatusBar(statusBarStyle, statusBarColor);
  const range = useViewportRange();
  const { ref: topBarRef, height: topBarHeight } = useElementHeight<HTMLDivElement>();
  // Viewport root, never the scroller: on single-column desktop the scroller is `overflow-y: visible`,
  // so a sentinel rooted there could never leave the root's box and would never fire. The viewport root
  // still covers mobile because IntersectionObserver clips the intersection rect by ancestor overflow.
  const [scrollSentinelRef, isAtScrollTop] = useScrollSentinel({ position: "top", threshold: 0 });

  const slots = useFrameSlots(children, SLOT_COMPONENTS);
  const contentProps = (slots.content?.props ?? {}) as {
    maxWidth?: FrameContentMaxWidth;
    centred?: boolean;
    fills?: boolean;
    padded?: boolean;
  };
  const topBarProps = (slots.topBar?.props ?? {}) as { sticky?: boolean };
  const bottomBarProps = (slots.bottomBar?.props ?? {}) as FrameBottomBarProps;
  const leftPaneProps = (slots.leftPane?.props ?? {}) as FramePaneProps;
  const rightPaneProps = (slots.rightPane?.props ?? {}) as FramePaneProps;

  const leftPaneCollapsed = resolvePaneCollapsed(leftPaneProps, range, "narrow");
  const rightPaneCollapsed = resolvePaneCollapsed(rightPaneProps, range, "regular");
  const splitView = (!!slots.leftPane && !leftPaneCollapsed) || (!!slots.rightPane && !rightPaneCollapsed);

  // Drill/bottomSheet panes render in the DOM at all viewports and rely on Tailwind to
  // hide them below `md` — keeps SSR'd HTML correct on mobile (no flash where the pane
  // briefly renders before hydration computes the viewport range).
  const leftPaneVisibility =
    leftPaneProps.collapsed === undefined &&
    (leftPaneProps.collapse === "drill" || leftPaneProps.collapse === "bottomSheet")
      ? "responsive"
      : "visible";

  // `fills` pages clip the scroller and nominate their own scroll container, which does its
  // own clearance maths — padding here would double-count.
  const floatingBottomBar = bottomBarProps.variant === "floating" && !contentProps.fills;

  const styles = frame({
    contentMaxWidth: contentProps.maxWidth,
    contentCentred: contentProps.centred,
    contentFills: contentProps.fills,
    contentPadded: contentProps.padded,
    floatingBottomBar,
    leftPaneWidth: leftPaneProps.maxWidth ?? "small",
    leftPanePadded: leftPaneProps.padded ?? false,
    leftPaneVisibility,
    rightPaneWidth: rightPaneProps.maxWidth ?? "small",
    rightPanePadded: rightPaneProps.padded ?? false,
    splitView,
    stickyTopBar: topBarProps.sticky ?? true,
  });

  const contextValue = useMemo<FrameContextValue>(
    () => ({
      hasFooter: !!slots.footer,
      hasBottomBar: !!slots.bottomBar,
      isScrolled: !isAtScrollTop,
    }),
    [slots.footer, slots.bottomBar, isAtScrollTop],
  );

  // Publish the measured top-bar height as a CSS custom property so the panes (and any
  // future slot that needs to compute against the visible body area) can use it via
  // `calc(100dvh - var(--frame-topbar-h, 5.5rem))`. Omit the variable until the layout
  // effect has measured so the fallback applies for SSR and the first client paint.
  const rootStyle = {
    ...(topBarHeight > 0 ? { "--frame-topbar-h": `${topBarHeight}px` } : {}),
    ...(floatingBottomBar ? { "--frame-bottombar-offset": BOTTOM_BAR_FLOATING_OFFSET } : {}),
  } as CSSProperties;

  return (
    <FrameContext value={contextValue}>
      <div className={styles.root({ className })} style={rootStyle}>
        {slots.background ? <div className={styles.background()}>{slots.background}</div> : null}

        {slots.topBar ? (
          <div ref={topBarRef} className={styles.topBar()} data-frame-chrome="topbar">
            {slots.topBar}
          </div>
        ) : null}

        <div className={styles.scroller()} {...SCROLL_REGION_ATTR}>
          {/* Publishes `isScrolled`: must stay the scroller's first child (and zero-height, so it
              adds no layout) — it leaves through the top edge exactly when content starts to pass
              under the chrome. */}
          <div ref={scrollSentinelRef} className="h-0 w-full shrink-0" />
          <div className={styles.columns()}>
            {slots.leftPane && (leftPaneVisibility === "responsive" || !leftPaneCollapsed) ? (
              <aside className={styles.leftPane()}>{slots.leftPane}</aside>
            ) : null}

            <main className={styles.main()}>
              <div className={styles.contentWrapper()}>{slots.content}</div>
            </main>

            {slots.rightPane && !rightPaneCollapsed ? (
              <aside className={styles.rightPane()}>{slots.rightPane}</aside>
            ) : null}
          </div>

          {slots.footer}
        </div>

        {slots.bottomBar}
        {slots.alerts ? <div className={styles.alerts()}>{slots.alerts}</div> : null}
        {slots.overlay}
      </div>
    </FrameContext>
  );
}

/** Compute whether a pane should be hidden at the current viewport range, respecting
 *  any explicit `collapsed` override the consumer passed. */
function resolvePaneCollapsed(
  paneProps: FramePaneProps,
  range: "narrow" | "regular" | "wide",
  hideBelow: "narrow" | "regular",
): boolean {
  if (paneProps.collapsed !== undefined) return paneProps.collapsed;
  // For now, both 'drill' and 'bottomSheet' simply hide at narrow viewports — the
  // bottomSheet trigger and 'stack' rendering are deferred.
  if (hideBelow === "regular" && range !== "wide") return true;
  if (hideBelow === "narrow" && range === "narrow") return true;
  return false;
}

export const Frame = Object.assign(FrameRoot, {
  Background: FrameBackground,
  TopBar: FrameTopBar,
  LeftPane: FrameLeftPane,
  Content: FrameContent,
  RightPane: FrameRightPane,
  Footer: FrameFooter,
  BottomBar: FrameBottomBar,
  Alerts: FrameAlerts,
  Overlay: FrameOverlay,
});
